import {html} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {state} from "lit/decorators/state.js";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {get} from "lit-translate";
import {container} from "tsyringe";
import {translateOr} from "../../helpers/translate-or.helper";

// Registering `<o2d-table>` (the tag string is not exported — importing defines it).
// Its CSS used to be pulled here as a separate side-effect import — Stefan,
// 2026-08-30: moved to ralphy.css, now o2d-table.css.
import '@o2d/table';
import type {O2dTableColumn} from '@o2d/table';

// Register the input-component tags so `<ha-input-text>` / `<ha-input-checkbox>` are defined
// even when this dialog is instantiated on demand (UiHelper createElement pattern).
import "../_inputs/ha-input-text/ha-input-text.component";
import "../_inputs/ha-input-checkbox/ha-input-checkbox.component";

import {AbstractDialogComponent} from "../ha-dialog/abstract-dialog.component";
import {CommandBus} from "../../services/command-bus.service";
import {RestorableRepository} from "../../domain/repositories/restorable.repository";
import {
    deletedAtLabel,
    deletedAtSortValue,
    deletedByLabel,
    restorableRowKey,
    type RestorableRow,
} from "../../domain/restorable-row.mapper";
import {RestoreEntityCommand} from "../../commands/restore-entity.command";

/** Filter debounce for the Papierkorb text search (mirrors the translation-editor pattern). */
const FILTER_DEBOUNCE_MS = 300;

/**
 * DISPLAY ORDER of the type toggles — and nothing else.
 *
 * It is NOT a whitelist and must never become one. The dialog's predecessor kept a hand-written
 * mirror of the backend's `RestoreTypeMap` (entity class + label per type) and gated every fetch
 * on it; when the backend added `linkview` and this list did not, restoring a board became
 * impossible with no visible sign of why. Here, the types come from the SERVER's answer: a key
 * missing from this list simply sorts last and is still shown, still filterable, still
 * restorable. The keys are exactly the `papierkorb.type.*` translation keys.
 */
const TYPE_DISPLAY_ORDER: readonly string[] = [
    'issue',
    'project',
    'employee',
    'keyword',
    'issue_status',
    'issue_type',
    'knowledge_object',
    'linkview',
];

/**
 * "Papierkorb" dialog (O2D-PRD-101): lists soft-deleted entities across the whitelisted types
 * and restores them.
 *
 * ONE request, for everything, through `RestorableRepository`. The previous version issued one
 * `/api/v1/query/<type>` per selected type, each with the soft-delete filter disabled — which
 * returns the LIVING rows too, so it downloaded the entire database and threw most of it away in
 * the browser, per type, on every toggle. Toggling a type now filters rows already in hand.
 */
@customElement('ha-papierkorb-dialog')
export class HaPapierkorbDialog extends AbstractDialogComponent {

    @state()
    private _filter = '';

    /**
     * The types the user has switched OFF, not the ones left on.
     *
     * Inverted deliberately: a type the server starts reporting tomorrow is then visible by
     * default instead of silently hidden because it was absent from a set built yesterday.
     */
    @state()
    private _deselectedTypes: Set<string> = new Set();

    /** The FULL stock from the last fetch. The type toggles filter this, they do not re-fetch. */
    @state()
    private _rows: RestorableRow[] = [];

    @state()
    private _restoringKeys: Set<string> = new Set();

    /**
     * Set when the fetch failed — a 403 above all — or when a restore was refused. Shown INSTEAD
     * of an empty table for the former, ABOVE the table for the latter. Cleared by the next
     * successful read.
     */
    @state()
    private _error: string | null = null;

    /**
     * Whitelist keys the server could not load (`failedTypes` in the answer). Their rows are
     * simply missing from `_rows`, so without this hint an empty type would look like
     * "nothing of that kind was deleted" — the same lie the 403 used to tell.
     */
    @state()
    private _failedTypes: string[] = [];

    @state()
    private _loading = false;

    /** Debounces filter keystrokes before they reach the table's `.filter` (one re-render per idle burst). */
    private readonly _filterSubject = new Subject<string>();

    public constructor() {
        super();
        this.closable = true;
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this._filterSubject
            .pipe(
                debounceTime(FILTER_DEBOUNCE_MS),
                distinctUntilChanged(),
                takeUntil(this._disconnected$),
            )
            .subscribe(value => this._filter = value);

        void this._reload();
    }

    /** The dialog title is rendered reactively so it re-translates on language change. */
    protected _renderHeader(): unknown {
        return get('papierkorb.title');
    }

    protected _renderMainContent(): unknown {
        const rows = this._visibleRows();

        return html`
            <div class="papierkorb">
                <div class="papierkorb__filters">
                    <div class="papierkorb__types">
                        ${this._presentTypes().map(type => html`
                            <ha-input-checkbox
                                is-toggle
                                .value=${!this._deselectedTypes.has(type)}
                                label=${this._typeLabel(type)}
                                @value-changed=${(event: CustomEvent<{ value: boolean }>) =>
                                    this._onTypeToggle(type, event.detail.value)}
                            ></ha-input-checkbox>
                        `)}
                    </div>
                    <ha-input-text
                        class="papierkorb__search"
                        with-clear-button
                        placeholder=${get('papierkorb.filter.placeholder')}
                        @value-changed=${(event: CustomEvent<{ value: string }>) =>
                            this._filterSubject.next(event.detail.value)}
                    ></ha-input-text>
                </div>
                ${this._renderStatus(rows)}
                ${this._renderFailedTypes()}
                <o2d-table
                    .columns=${this._buildColumns()}
                    .rows=${rows}
                    .rowKey=${(row: unknown) => restorableRowKey(row as RestorableRow)}
                    .filter=${this._filter}
                ></o2d-table>
            </div>
        `;
    }

    /**
     * The failure and the empty state, which are NOT the same thing and must not look alike.
     *
     * A 403 used to be caught and turned into an empty row list, so "you are not allowed to see
     * this" and "nothing has been deleted" rendered identically — the user had no way to tell
     * that anything had gone wrong at all.
     */
    private _renderStatus(rows: RestorableRow[]): unknown {
        if (this._error !== null) {
            return html`<p class="papierkorb__error" role="alert">${this._error}</p>`;
        }

        if (!this._loading && rows.length === 0) {
            return html`<p class="papierkorb__empty">${translateOr('papierkorb.empty', 'Der Papierkorb ist leer.')}</p>`;
        }

        return null;
    }

    /**
     * The partial-failure hint: rows of the named types are MISSING, everything else is there.
     * Rendered in addition to the table, never instead of it — the loaded rows are still
     * restorable and must stay reachable.
     */
    private _renderFailedTypes(): unknown {
        if (this._failedTypes.length === 0) {
            return null;
        }

        const types = this._failedTypes.map(type => this._typeLabel(type)).join(', ');

        return html`<p class="papierkorb__failed-types" role="alert">${translateOr(
            'papierkorb.error.failed-types',
            `Einträge dieser Art konnten nicht geladen werden: ${types}`,
            {types},
        )}</p>`;
    }

    /**
     * Five columns: type, label, when it was deleted, who deleted it, and the restore action.
     */
    private _buildColumns(): O2dTableColumn[] {
        const unknown = this._unknownLabel();

        return [
            {
                id: 'type',
                header: get('papierkorb.column.type'),
                width: '180px',
                sortable: true,
                filterValue: row => this._typeLabel((row as RestorableRow).entityType),
            },
            {
                id: 'entity',
                header: get('papierkorb.column.entity'),
                sortable: true,
                filterValue: row => (row as RestorableRow).label,
            },
            {
                id: 'deletedAt',
                header: translateOr('papierkorb.column.deleted-at', 'Gelöscht am'),
                width: '190px',
                sortable: true,
                filterValue: row => deletedAtLabel(row as RestorableRow, unknown),
                // WITHOUT this the table would sort by `filterValue` — the LOCALIZED text
                // (`controllers/column-accessor.ts:11`) — and order "1. Feb." before "1. Jan.".
                sortValue: row => deletedAtSortValue(row as RestorableRow),
            },
            {
                id: 'deletedBy',
                header: translateOr('papierkorb.column.deleted-by', 'Gelöscht von'),
                width: '200px',
                sortable: true,
                filterValue: row => deletedByLabel(row as RestorableRow, unknown),
            },
            {
                id: 'action',
                header: '',
                width: '64px',
                filterValue: () => '',
                cellRenderer: row => this._renderRestoreButton(row as RestorableRow),
            },
        ];
    }

    private _renderRestoreButton(row: RestorableRow): ReturnType<typeof html> {
        return html`
            <button
                type="button"
                class="papierkorb__restore"
                title=${get('papierkorb.restore')}
                ?disabled=${this._restoringKeys.has(restorableRowKey(row))}
                @click=${() => this._onRestore(row)}
            >
                <i class="icon icon-trash-arrow-up"></i>
            </button>
        `;
    }

    /** The types actually present in the current stock, in the fixed display order (unknown last). */
    private _presentTypes(): string[] {
        const present = Array.from(new Set(this._rows.map(row => row.entityType)));
        const rank = (type: string): number => {
            const index = TYPE_DISPLAY_ORDER.indexOf(type);
            return index === -1 ? TYPE_DISPLAY_ORDER.length : index;
        };

        return present.sort((left, right) => rank(left) - rank(right) || left.localeCompare(right));
    }

    /** Applies the type toggles to the full stock — in the browser, without a request. */
    private _visibleRows(): RestorableRow[] {
        return this._rows.filter(row => !this._deselectedTypes.has(row.entityType));
    }

    /** Resolves the i18n label for a type key (falls back to the raw key if untranslated). */
    private _typeLabel(entityType: string): string {
        return translateOr(`papierkorb.type.${entityType}`, entityType);
    }

    private _unknownLabel(): string {
        return translateOr('papierkorb.unknown', 'unbekannt');
    }

    private _onTypeToggle(entityType: string, selected: boolean): void {
        const deselected = new Set(this._deselectedTypes);
        if (selected) {
            deselected.delete(entityType);
        } else {
            deselected.add(entityType);
        }
        this._deselectedTypes = deselected;
    }

    /**
     * Restores one entry. Guarded against double-clicks, then RE-FETCHES.
     *
     * Not an optimistic row removal: `RestoreEntityCommandHandler` is idempotent
     * (`RestoreEntityCommandHandler.php:63`), so restoring something a second browser tab already
     * restored succeeds and would have left the stale row sitting in the list. A re-read is the
     * only thing that says what is actually still deleted.
     *
     * A refused restore is SHOWN, on both of its two paths: `dispatchCommand` resolves `false`
     * for a `success: false` answer (403, unknown type, conflict) and does NOT throw
     * (`command-bus.service.ts:70-76`), while a transport failure rejects. Either one used to end
     * up on the console only, and to the user it looked like nothing had happened — button
     * active again, row still there. Both now land in `_error`, the same place the load failure
     * already uses.
     */
    private async _onRestore(row: RestorableRow): Promise<void> {
        const key = restorableRowKey(row);
        if (this._restoringKeys.has(key)) {
            return;
        }

        this._restoringKeys = new Set(this._restoringKeys).add(key);

        try {
            const restored = await container.resolve(CommandBus).dispatchCommand(new RestoreEntityCommand(row));
            if (!restored) {
                this._showRestoreError(row, 'the command was refused');
                return;
            }
            await this._reload();
        } catch (error) {
            this._showRestoreError(row, error);
        } finally {
            const keys = new Set(this._restoringKeys);
            keys.delete(key);
            this._restoringKeys = keys;
        }
    }

    private _showRestoreError(row: RestorableRow, reason: unknown): void {
        console.error('[ha-papierkorb-dialog] restore failed', restorableRowKey(row), reason);
        this._error = translateOr(
            'papierkorb.error.restore',
            'Der Eintrag konnte nicht wiederhergestellt werden.',
        );
    }

    /** ONE request for every type. The failure is kept, not swallowed. */
    private async _reload(): Promise<void> {
        this._loading = true;
        try {
            const stock = await container.resolve(RestorableRepository).findAll();
            this._rows = stock.rows;
            this._failedTypes = stock.failedTypes;
            this._error = null;
        } catch (error) {
            console.error('[ha-papierkorb-dialog] failed to load trash', error);
            this._rows = [];
            this._failedTypes = [];
            this._error = translateOr(
                'papierkorb.error.load',
                'Der Papierkorb konnte nicht geladen werden.',
            );
        } finally {
            this._loading = false;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ha-papierkorb-dialog': HaPapierkorbDialog;
    }
}
